import { useState, useRef, useEffect } from "react";
import {
  getSessionErrors,
  createSessionError,
  deleteSessionError,
  useQuery,
  useAction,
} from "wasp/client/operations";
import useTranslation from "../client/hooks/useTranslation";
import { useToast } from "../client/hooks/use-toast";
import { errorPastilleClass } from "./sessionUtils";
import { X, Check, Trash2, ChevronDown, Plus } from "lucide-react";

export interface SessionErrorOption {
  id: string;
  name: string;
}

interface SessionErrorSelectProps {
  /** Currently selected error ids. */
  value: string[];
  /** Called with the new full list of selected ids whenever selection changes. */
  onChange: (errorIds: string[]) => void;
}

/**
 * Multi-select dropdown of error pastilles for the session evaluation UI.
 * Lists the user's whole catalog with checkboxes, supports inline creation of
 * a new error, and shows selected errors as removable pastilles below.
 */
export function SessionErrorSelect({ value, onChange }: SessionErrorSelectProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: catalog } = useQuery(getSessionErrors);
  const createErrorFn = useAction(createSessionError);
  const deleteErrorFn = useAction(deleteSessionError);

  const [isOpen, setIsOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Close the dropdown when clicking outside or pressing Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setConfirmDeleteId(null);
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setConfirmDeleteId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [isOpen]);

  const options: SessionErrorOption[] = catalog ?? [];
  const selectedOptions = options.filter((o) => value.includes(o.id));

  const toggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const handleCreate = async () => {
    if (isCreating) return;
    const name = newName.trim();
    if (!name) return;
    setIsCreating(true);
    try {
      await createErrorFn({ name });
      if (!isMountedRef.current) return;
      setNewName("");
    } catch {
      if (!isMountedRef.current) return;
      toast({ title: t.sessionDetail.saveError, variant: "destructive" });
    } finally {
      if (isMountedRef.current) setIsCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteErrorFn({ id });
      if (!isMountedRef.current) return;
      setConfirmDeleteId(null);
      if (value.includes(id)) {
        onChange(value.filter((v) => v !== id));
      }
    } catch {
      if (!isMountedRef.current) return;
      toast({ title: t.sessionDetail.saveError, variant: "destructive" });
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => {
          setIsOpen((o) => !o);
          setConfirmDeleteId(null);
        }}
        className="w-full flex items-center justify-between bg-transparent border-0 border-b border-border text-sm py-1 focus:outline-none focus:border-primary transition-colors"
      >
        <span className={selectedOptions.length === 0 ? "text-muted-foreground" : ""}>
          {selectedOptions.length === 0
            ? t.session.errorsPlaceholder
            : `${selectedOptions.length} ${t.session.errors}`}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-card shadow-lg p-1">
          <div className="max-h-48 overflow-y-auto">
            {options.map((opt) => {
              const checked = value.includes(opt.id);
              return (
                <div
                  key={opt.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-muted/50"
                >
                  <button
                    type="button"
                    onClick={() => toggle(opt.id)}
                    className="flex items-center gap-2 flex-1 text-left text-sm"
                  >
                    <span className="h-4 w-4 flex items-center justify-center text-primary">
                      {checked && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className={checked ? "text-foreground" : "text-muted-foreground"}>
                      {opt.name}
                    </span>
                  </button>
                  {confirmDeleteId === opt.id ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(opt.id)}
                      className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                      title={t.session.errorDeleteConfirm}
                    >
                      {t.session.errorDeleteConfirm}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(opt.id)}
                      className="text-muted-foreground hover:text-foreground"
                      title={t.session.errorDelete}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-1 border-t border-border mt-1 pt-1 px-2">
            <input
              type="text"
              value={newName}
              maxLength={40}
              onChange={(e) => setNewName(e.target.value.slice(0, 40))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isCreating) {
                  e.preventDefault();
                  void handleCreate();
                }
              }}
              placeholder={t.session.errorNamePlaceholder}
              className="flex-1 min-w-0 bg-transparent border-0 text-sm py-1 focus:outline-none focus:ring-0 placeholder:text-muted-foreground"
            />
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={isCreating || !newName.trim()}
              aria-label={t.session.errorCreateNew}
              title={t.session.errorCreateNew}
              className="shrink-0 h-6 w-6 rounded flex items-center justify-center text-primary hover:bg-muted/50 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedOptions.map((opt) => (
            <span key={opt.id} className={errorPastilleClass}>
              {opt.name}
              <button
                type="button"
                onClick={() => toggle(opt.id)}
                aria-label={t.session.errorDelete}
                className="hover:text-red-700"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
